import { Dispatch, FC, Fragment, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { Button, Icon, SortableList } from '@components/base';
import { OrderBy } from '@models/OrderBy';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { getSortableFieldDefs } from '@modules/layouts/hooks/useSearchAndFilter/utils';

import OrderByFieldSelect from './OrderByFieldSelect';
import SortOrderRow from './OrderByRow';
import { getNewEntry } from './utils';

const Loading: FC = () => (
  <Box alignItems='center' display='flex' justifyContent='center' minWidth={160} p={4}>
    <CircularProgress />
  </Box>
);

type OrderByPopupContentProps = {
  orderBy: OrderBy[];
  setOrderBy: Dispatch<SetStateAction<OrderBy[]>>;
};

const OrderByPopupContent: FC<OrderByPopupContentProps> = ({ orderBy, setOrderBy }) => {
  const { t } = useTranslation();

  const {
    filterDefLoad: { error, isLoading: filterDefsLoading, data: filterDefs },
  } = useSearchAndFilter();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [displayNewField, setDisplayNewField] = useState(false);

  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    setDisplayNewField(false);
  }, [orderBy.length, anchorEl]);

  const handleDeleteRow = (id: string) => () =>
    setOrderBy(prev => {
      const newVal = prev.filter(i => i.id !== id);
      if (newVal.length === 0) handleClose();
      return newVal;
    });

  const handleAdd = (field: string) => {
    setDisplayNewField(false);
    setOrderBy(prev => [...prev, getNewEntry(field)]);
  };

  const sortableFieldDefs = getSortableFieldDefs(filterDefs);

  const disableAddMore = orderBy.length > 0 && orderBy.length === sortableFieldDefs?.length;

  return filterDefsLoading ? (
    <Loading />
  ) : error ? (
    <Stack minWidth={160} p={2} spacing={2}>
      <Typography>{t('errorMessage')}</Typography>
    </Stack>
  ) : (
    <Fragment>
      {(orderBy.length > 0 || displayNewField) && (
        <Box mb={-1}>
          <List>
            <SortableList
              indicateOnDrag
              items={orderBy}
              renderItem={item => (
                <SortOrderRow
                  handleDeleteRow={handleDeleteRow(item.id)}
                  inset={displayNewField}
                  setOrderBy={setOrderBy}
                  {...item}
                />
              )}
              setItems={setOrderBy}
            />

            {displayNewField && (
              <Collapse appear in>
                <ListItem dense>
                  <Box ml={-0.5} mr={0.5}>
                    <IconButton
                      aria-label={t('cancel')}
                      onClick={() => setDisplayNewField(false)}
                      size='small'
                    >
                      <Icon fontSize='inherit' name='close' />
                    </IconButton>
                  </Box>

                  <ListItemText>
                    <OrderByFieldSelect onSelect={handleAdd} />
                  </ListItemText>
                </ListItem>
              </Collapse>
            )}
          </List>
        </Box>
      )}

      <Stack direction='row' justifyContent='space-between' p={1}>
        <Button
          disabled={displayNewField || disableAddMore}
          leftIcon='add'
          onClick={() => setDisplayNewField(true)}
        >
          {t('add')}
        </Button>

        <Button onClick={() => setOrderBy([])}>{t('reset')}</Button>
      </Stack>
    </Fragment>
  );
};

export default OrderByPopupContent;
